import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, PlusIcon, ChevronDownIcon, ArrowRightOnRectangleIcon, QuestionMarkCircleIcon, BellIcon, SparklesIcon, CurrencyDollarIcon, EnvelopeIcon, MapIcon, ChatBubbleLeftIcon, UserCircleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTourContext } from '@/components/tour/TourContext';
import { useAppStore } from '@/stores/appStore';
import { useAuth, useIsAdmin, useIsEditor } from '@/contexts/AuthContext';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { notificationApi, organizationApi, Notification } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';
import { AiInitiativeModal } from '@/components/modals/AiInitiativeModal';
import { AiCreditsModal } from '@/components/modals/AiCreditsModal';
import { ReferralModal } from '@/components/modals/ReferralModal';
import { EditProfileModal } from '@/components/modals/EditProfileModal';
import { GlobalNotification } from '@/components/ui/GlobalNotification';
import { ViewSwitcher } from '@/components/layout/ViewSwitcher';

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openAddInitiativeModal, triggerRefresh, unreadNotificationCount, setUnreadNotificationCount, initiatives } = useAppStore();
  const { user, logout } = useAuth();
  const isAdmin = useIsAdmin();
  const canEdit = useIsEditor(); // Editors and Admins can add initiatives

  const labs = useLabsFeatures();
  const fc = useFieldConfig();
  const tourCtx = useTourContext();
  const notificationsEnabled = labs.isEnabled('notifications');
  const savedViewsEnabled = labs.isEnabled('savedViews');

  const loadAccessibleViews = useAppStore((s) => s.loadAccessibleViews);
  const accessibleViews = useAppStore((s) => s.accessibleViews);
  const accessibleViewsLoaded = useAppStore((s) => s.accessibleViewsLoaded);
  const setActiveSavedView = useAppStore((s) => s.setActiveSavedView);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isReportsMenuOpen, setIsReportsMenuOpen] = useState(false);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [bellNotifications, setBellNotifications] = useState<Notification[]>([]);
  const [bellLoading, setBellLoading] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);
  const aiUsage = useAppStore((s) => s.aiUsage);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reportsMenuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const showAuditLog    = labs.isEnabled('auditLog');
  const showComments    = labs.isEnabled('comments');
  const showDependencies = labs.isEnabled('dependencies');
  const showScoring = labs.isEnabled('scoring');
  const showBatchUpdate = labs.isEnabled('batchUpdate') && isAdmin;
  const hasAnyReport = showAuditLog || showComments || showDependencies || showScoring;
  const showReports = hasAnyReport || showBatchUpdate;

  const showTimeline = labs.isEnabled('viewTimeline');
  const showList = labs.isEnabled('viewList');
  const showKanban = labs.isEnabled('viewKanban');
  const showCalendar = labs.isEnabled('viewCalendar');

  const isHome = location.pathname === '/' || location.pathname === '/home';
  const isTimeline = location.pathname === '/timeline';
  const isList = location.pathname === '/list';
  const isKanban = location.pathname === '/kanban';
  const isCalendar = location.pathname === '/calendar';
  const isAuditLog = location.pathname === '/auditlog';
  const isCommentsLog = location.pathname === '/commentslog';
  const isDependencyLog = location.pathname === '/dependencies';
  const isScoringLog = location.pathname === '/scoring' || location.pathname === '/scoring/chart';
  const isBatchUpdate = location.pathname === '/batch-update';
  const isReports = (isAuditLog && showAuditLog) || (isCommentsLog && showComments) || (isDependencyLog && showDependencies) || (isScoringLog && showScoring) || (isBatchUpdate && showBatchUpdate);
  const isSettings = location.pathname === '/settings';
  const isIntelligence = location.pathname === '/intelligence';

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reportsMenuRef.current && !reportsMenuRef.current.contains(event.target as Node)) {
        setIsReportsMenuOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setIsBellOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close the mobile drawer on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch org name for personalized nav label and user menu
  useEffect(() => {
    organizationApi.get()
      .then(org => setOrgName(org.name))
      .catch(() => {});
  }, []);

  // Load the user's accessible named views once the Saved Views lab is on
  useEffect(() => {
    if (savedViewsEnabled) {
      loadAccessibleViews();
    }
  }, [savedViewsEnabled, loadAccessibleViews]);

  // Auto-load the user's pinned favorite view whenever they land on Timeline, List, Kanban or
  // Calendar. Keyed on pathname (not activeSavedView) so a manual caret switch sticks while you
  // stay on the page, but returning to the view re-applies your favorite. Falls back to
  // Default when there's no (accessible) favorite.
  useEffect(() => {
    if (!savedViewsEnabled || !accessibleViewsLoaded) return;
    // If the user's last intent was an explicit pick from the caret menu, honor that choice and
    // don't override it with their pinned favorite. This flag is standing intent (set true on an
    // explicit select, false on a label click) — we only READ it here and never reset it, so it
    // survives React StrictMode's double-invocation of this effect in dev.
    if (useAppStore.getState().skipFavoriteLoad) return;
    const type =
      location.pathname === '/timeline' ? 'timeline'
      : location.pathname === '/list' ? 'list'
      : location.pathname === '/kanban' ? 'kanban'
      : location.pathname === '/calendar' ? 'calendar'
      : null;
    if (!type) return;
    // A page refresh should keep the user on the view they last chose. We persist the explicit
    // choice per type in localStorage; if present it takes precedence over the pinned favorite.
    const activeKey = user?.id ? `cp_active_view_${type}_${user.id}` : null;
    const persisted = activeKey ? localStorage.getItem(activeKey) : null;
    if (persisted) {
      if (persisted === '__default__') { setActiveSavedView(null); return; }
      const chosen = accessibleViews.find((v) => v.id === persisted && v.viewType === type);
      setActiveSavedView(chosen ?? null);
      return;
    }
    const favId =
      type === 'timeline' ? user?.favoriteTimelineViewId
      : type === 'list' ? user?.favoriteListViewId
      : type === 'kanban' ? user?.favoriteKanbanViewId
      : user?.favoriteCalendarViewId;
    const fav = favId ? accessibleViews.find((v) => v.id === favId && v.viewType === type) : null;
    setActiveSavedView(fav ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, accessibleViewsLoaded, savedViewsEnabled, user?.favoriteTimelineViewId, user?.favoriteListViewId, user?.favoriteKanbanViewId, user?.favoriteCalendarViewId]);

  // Poll unread notification count every 60 seconds when notifications lab is enabled
  const pollUnreadCount = useCallback(async () => {
    if (!notificationsEnabled) return;
    try {
      const { unread } = await notificationApi.count();
      setUnreadNotificationCount(unread);
    } catch {
      // ignore polling errors
    }
  }, [notificationsEnabled, setUnreadNotificationCount]);

  useEffect(() => {
    if (!notificationsEnabled) return;
    pollUnreadCount();
    const interval = setInterval(pollUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [notificationsEnabled, pollUnreadCount]);

  const openBell = async () => {
    setIsBellOpen((prev) => !prev);
    if (!isBellOpen) {
      setBellLoading(true);
      try {
        const { notifications } = await notificationApi.list(5, 0);
        setBellNotifications(notifications);
      } catch {
        // ignore
      } finally {
        setBellLoading(false);
      }
    }
  };

  const handleMarkAllRead = async () => {
    await notificationApi.readAll().catch(() => {});
    setBellNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadNotificationCount(0);
  };

  const handleMarkOneRead = async (id: string) => {
    await notificationApi.readOne(id).catch(() => {});
    setBellNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    setUnreadNotificationCount(Math.max(0, unreadNotificationCount - 1));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCreateWithAi = () => {
    if (aiUsage && !aiUsage.isCustomMode) {
      const outOfFree = aiUsage.createAiUsed >= aiUsage.createAiLimit;
      const outOfPaid = aiUsage.paidAiCredits <= 0;
      if (outOfFree && outOfPaid) { setIsCreditsModalOpen(true); return; }
    }
    setIsAiModalOpen(true);
  };

  const navLinkClass = (active: boolean) =>
    `px-3 py-2 text-sm font-medium transition-colors ${
      active
        ? 'text-blue-600'
        : 'text-gray-600 hover:text-gray-900'
    }`;

  // Full-width row style for links inside the mobile drawer
  const mobileLinkClass = (active: boolean) =>
    `block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-blue-50 text-blue-600'
        : 'text-gray-700 hover:bg-gray-50'
    }`;

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-purple-100 text-purple-700';
      case 'EDITOR':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <>
    <nav className="bg-white border-b border-gray-200">
      <div className="px-3 xl:px-6 py-3 relative" ref={mobileMenuRef}>
        <GlobalNotification />
        <div className="flex items-center justify-between">
          {/* Left: Hamburger (mobile), Logo, and Navigation */}
          <div className="flex items-center gap-1 min-w-0">
            {/* Mobile menu toggle — the full nav-links row is desktop-only (see `hidden lg:flex`
                below); below the `lg` breakpoint this drawer is the only way to reach them.
                mobileMenuRef wraps this WHOLE top-level container (button + drawer together, see
                below) so clicking a link inside the open drawer isn't treated as an outside click. */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen((o) => !o)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                aria-label="Open menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
              </button>
            </div>

            {/* Logo/Home Link */}
            <Link
              to="/home"
              title={orgName && orgName.length > 20 ? orgName : undefined}
              className="flex items-center gap-2 px-2 lg:px-3 py-2 rounded-md transition-colors hover:bg-gray-100 min-w-0"
            >
              <HomeIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <span className={`font-bold text-lg truncate ${isHome ? 'text-blue-600' : 'text-gray-900'}`}>
                {orgName && orgName.length <= 20 ? orgName : 'Home'}
              </span>
            </Link>

            {/* Navigation Links — desktop only; see the mobile drawer below for the phone/tablet equivalent */}
            <div className="hidden lg:flex items-center gap-0 xl:gap-1 ml-1 xl:ml-2">
              {showTimeline && (
                <ViewSwitcher viewType="timeline" to="/timeline" label="Timeline" active={isTimeline} navLinkClass={navLinkClass} />
              )}
              {showList && (
                <ViewSwitcher viewType="list" to="/list" label="List" active={isList} navLinkClass={navLinkClass} />
              )}
              {showKanban && (
                <ViewSwitcher viewType="kanban" to="/kanban" label="Kanban" active={isKanban} navLinkClass={navLinkClass} />
              )}
              {showCalendar && (
                <ViewSwitcher viewType="calendar" to="/calendar" label="Calendar" active={isCalendar} navLinkClass={navLinkClass} />
              )}

              {/* Reports Dropdown — only visible when at least one report is enabled */}
              {showReports && (
                <div className="relative" ref={reportsMenuRef}>
                  <button
                    onClick={() => setIsReportsMenuOpen(!isReportsMenuOpen)}
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                      isReports
                        ? 'text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Reports
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isReportsMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isReportsMenuOpen && (
                    <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {showAuditLog && (
                        <Link
                          to="/auditlog"
                          onClick={() => setIsReportsMenuOpen(false)}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isAuditLog
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Audit Log
                        </Link>
                      )}
                      {showComments && (
                        <Link
                          to="/commentslog"
                          onClick={() => setIsReportsMenuOpen(false)}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isCommentsLog
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Comments Log
                        </Link>
                      )}
                      {showDependencies && (
                        <Link
                          to="/dependencies"
                          onClick={() => setIsReportsMenuOpen(false)}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isDependencyLog
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Dependency Log
                        </Link>
                      )}
                      {showScoring && (
                        <Link
                          to="/scoring"
                          onClick={() => setIsReportsMenuOpen(false)}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isScoringLog
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Scoring
                        </Link>
                      )}
                      {showBatchUpdate && (
                        <>
                          {hasAnyReport && <div className="my-1 border-t border-gray-100" />}
                          <Link
                            to="/batch-update"
                            onClick={() => setIsReportsMenuOpen(false)}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              isBatchUpdate
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Batch Update
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(labs.isEnabled('intelligenceHealth') || labs.isEnabled('intelligenceAsk') || labs.isEnabled('intelligenceDigest')) && (
                <Link to="/intelligence" className={`flex items-center gap-1.5 ${navLinkClass(isIntelligence)}`}>
                  <SparklesIcon className={`w-4 h-4 ${isIntelligence ? 'text-amber-500' : 'text-amber-400'}`} />
                  Intelligence
                </Link>
              )}

              {isAdmin && (
                <Link to="/settings" className={navLinkClass(isSettings)}>
                  Settings
                </Link>
              )}
            </div>
          </div>

          {/* Right: Add Initiative, Bell, User Menu */}
          <div className="flex items-center gap-1.5 xl:gap-3">
            {/* Desktop only — the mobile drawer has its own Create with AI / Add Initiative row */}
            {canEdit && (
              <div className="hidden lg:flex items-center gap-1.5 xl:gap-3">
                {labs.isEnabled('intelligenceCreate') && (() => {
                const outOfFree = !!(aiUsage && !aiUsage.isCustomMode && aiUsage.createAiUsed >= aiUsage.createAiLimit);
                const outOfPaid = !!(aiUsage && !aiUsage.isCustomMode && aiUsage.paidAiCredits <= 0);
                const showEarnLink = outOfFree && outOfPaid;
                return (
                  <div className="relative flex flex-col items-center">
                    <button
                      onClick={handleCreateWithAi}
                      className="flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-md transition-colors"
                      title="Create initiative with AI"
                    >
                      <SparklesIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="hidden xl:inline">Create with AI</span>
                      {aiUsage && !aiUsage.isCustomMode && aiUsage.createAiUsed < aiUsage.createAiLimit && aiUsage.createAiLimit - aiUsage.createAiUsed <= 3 && (
                        <span className="hidden xl:inline text-xs text-amber-600 font-normal">· {aiUsage.createAiLimit - aiUsage.createAiUsed} left</span>
                      )}
                    </button>
                    {showEarnLink && (
                      <button
                        onClick={() => setIsReferralModalOpen(true)}
                        className="absolute -bottom-4 text-[10px] text-amber-600 hover:text-amber-700 hover:underline whitespace-nowrap"
                      >
                        Earn Credits
                      </button>
                    )}
                  </div>
                );
              })()}
                <button
                  data-tour-id="add-initiative-btn"
                  onClick={openAddInitiativeModal}
                  className="flex items-center gap-2 px-2.5 xl:px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add {fc.label('initiative')}
                </button>
              </div>
            )}

            {/* Notification Bell */}
            {notificationsEnabled && (
              <div className="relative" ref={bellRef}>
                <button
                  onClick={openBell}
                  className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Notifications"
                >
                  <BellIcon className="w-5 h-5" />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                </button>

                {isBellOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <span className="text-sm font-semibold text-gray-900">Notifications</span>
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Mark all read
                      </button>
                    </div>

                    {/* List */}
                    <div className="p-2 space-y-1.5 max-h-80 overflow-y-auto">
                      {bellLoading ? (
                        <div className="flex items-center justify-center h-24 text-sm text-gray-400">
                          Loading...
                        </div>
                      ) : bellNotifications.length === 0 ? (
                        <div className="flex items-center justify-center h-24 text-sm text-gray-400">
                          No notifications yet
                        </div>
                      ) : (
                        bellNotifications.map((n) => {
                          const isUnread = !n.isRead;
                          const timeAgo = formatDistanceToNow(new Date(n.createdAt), { addSuffix: true });
                          const init = initiatives.find((i) => i.id === n.initiativeId) as any;
                          const path = init?.subProduct?.product?.name && init?.subProduct?.name
                            ? `${init.subProduct.product.name} › ${init.subProduct.name}`
                            : (init?.subProduct?.name || '');
                          // Prefer the live relation title (initiative may have been renamed since);
                          // fall back to the snapshot, which survives initiative deletion.
                          const initiativeTitle = n.initiative?.title || n.initiativeTitle || '';

                          return (
                            <div
                              key={n.id}
                              onClick={() => handleMarkOneRead(n.id)}
                              className={`rounded-lg px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors border ${
                                isUnread ? 'bg-blue-50/40 border-blue-100' : 'bg-white border-gray-100'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${isUnread ? 'bg-blue-500' : 'bg-transparent'}`} />
                                {n.type === 'assigned'
                                  ? <UserCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" title="Assigned to you" />
                                  : <ChatBubbleLeftIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                }
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline justify-between gap-1">
                                    {n.type === 'assigned' ? (
                                      <span className="text-xs font-medium text-gray-900 truncate">{initiativeTitle}</span>
                                    ) : (
                                      <p className="text-xs truncate">
                                        <span className="font-semibold text-blue-600">{n.actorName}</span>
                                        <span className="text-gray-600"> mentioned you</span>
                                      </p>
                                    )}
                                    <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeAgo}</span>
                                  </div>
                                  {n.type !== 'assigned' && initiativeTitle && (
                                    <p className="text-xs text-gray-600 truncate">{initiativeTitle}</p>
                                  )}
                                  {n.type === 'mention' && n.commentPreview && (
                                    <p className="text-xs text-gray-500 truncate italic">"{n.commentPreview}"</p>
                                  )}
                                  {path && <p className="text-[11px] text-gray-400 truncate">{path}</p>}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 border-t border-gray-100 text-center">
                      <Link
                        to="/notifications"
                        onClick={() => setIsBellOpen(false)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View all notifications →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                data-tour-id="user-menu-btn"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-medium">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {user?.email}
                    </p>
                    {orgName && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {orgName}
                      </p>
                    )}
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role}
                    </span>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <button
                      onClick={() => { setIsUserMenuOpen(false); setIsEditProfileOpen(true); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <UserCircleIcon className="w-4 h-4" />
                      Edit Profile
                    </button>
                    <button
                      data-tour-id="refer-earn-btn"
                      onClick={() => { setIsUserMenuOpen(false); setIsReferralModalOpen(true); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                    >
                      <CurrencyDollarIcon className="w-4 h-4" />
                      Refer & Earn AI Credits
                    </button>
                    <Link
                      to="/help"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <QuestionMarkCircleIcon className="w-4 h-4" />
                      Help & Guide
                    </Link>
                    {tourCtx && (
                      <button
                        onClick={() => { setIsUserMenuOpen(false); tourCtx.startTour(user?.role ?? 'VIEWER'); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <MapIcon className="w-4 h-4" />
                        App Tour
                      </button>
                    )}
                    <a
                      href={`mailto:support@tabillo.com?subject=Support%20Request&body=Hi%20Team%2C%0A%0A${encodeURIComponent(window.location.host)}`}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <EnvelopeIcon className="w-4 h-4" />
                      Contact Support
                    </a>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile drawer — the phone/tablet equivalent of the desktop nav-links row above.
            Named-view switching (the ViewSwitcher caret) isn't reproduced here; it links to the
            default view for each type to keep this list simple at narrow widths. */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-3 pt-3 border-t border-gray-200 space-y-0.5">
            {canEdit && (
              <div className="flex items-center gap-2 pb-2 mb-1 border-b border-gray-100">
                {labs.isEnabled('intelligenceCreate') && (
                  <button
                    onClick={handleCreateWithAi}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-md transition-colors"
                  >
                    <SparklesIcon className="w-4 h-4 flex-shrink-0" />
                    Create with AI
                  </button>
                )}
                <button
                  onClick={openAddInitiativeModal}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add {fc.label('initiative')}
                </button>
              </div>
            )}

            {showTimeline && <Link to="/timeline" className={mobileLinkClass(isTimeline)}>Timeline</Link>}
            {showList && <Link to="/list" className={mobileLinkClass(isList)}>List</Link>}
            {showKanban && <Link to="/kanban" className={mobileLinkClass(isKanban)}>Kanban</Link>}
            {showCalendar && <Link to="/calendar" className={mobileLinkClass(isCalendar)}>Calendar</Link>}

            {showReports && (
              <>
                <div className="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Reports
                </div>
                {showAuditLog && <Link to="/auditlog" className={mobileLinkClass(isAuditLog)}>Audit Log</Link>}
                {showComments && <Link to="/commentslog" className={mobileLinkClass(isCommentsLog)}>Comments Log</Link>}
                {showDependencies && <Link to="/dependencies" className={mobileLinkClass(isDependencyLog)}>Dependency Log</Link>}
                {showScoring && <Link to="/scoring" className={mobileLinkClass(isScoringLog)}>Scoring</Link>}
                {showBatchUpdate && <Link to="/batch-update" className={mobileLinkClass(isBatchUpdate)}>Batch Update</Link>}
              </>
            )}

            {(labs.isEnabled('intelligenceHealth') || labs.isEnabled('intelligenceAsk') || labs.isEnabled('intelligenceDigest')) && (
              <Link to="/intelligence" className={`flex items-center gap-1.5 ${mobileLinkClass(isIntelligence)}`}>
                <SparklesIcon className={`w-4 h-4 ${isIntelligence ? 'text-amber-500' : 'text-amber-400'}`} />
                Intelligence
              </Link>
            )}

            {isAdmin && (
              <Link to="/settings" className={mobileLinkClass(isSettings)}>Settings</Link>
            )}
          </div>
        )}
      </div>
    </nav>

    <AiInitiativeModal
      isOpen={isAiModalOpen}
      onClose={() => setIsAiModalOpen(false)}
      onSuccess={() => { setIsAiModalOpen(false); triggerRefresh(); }}
    />
    <AiCreditsModal
      isOpen={isCreditsModalOpen}
      onClose={() => setIsCreditsModalOpen(false)}
      creditType="create"
    />
    <ReferralModal
      isOpen={isReferralModalOpen}
      onClose={() => setIsReferralModalOpen(false)}
    />
    <EditProfileModal
      isOpen={isEditProfileOpen}
      onClose={() => setIsEditProfileOpen(false)}
    />
  </>
  );
}
